import { memo } from 'react'
import { useLocation } from 'react-router'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { SidebarSubMenuItem } from '../menu-item/sub-menu-item'
import { SidebarItem, SidebarThemes } from '../types'

interface SidebarDropdownItemProps {
  item: SidebarItem
  theme?: SidebarThemes
}

export const SidebarDropdownItem = memo<SidebarDropdownItemProps>(
  ({ item, theme = 'light' }: SidebarDropdownItemProps) => {
    const { icon, title, subItems } = item

    const { pathname } = useLocation()

    const isActive = item.path.includes(pathname)

    return (
      <li>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex">
              <Tooltip delayDuration={200}>
                <TooltipTrigger
                  className={cn(
                    'mx-4 flex-1 items-center font-normal',
                    isActive
                      ? theme === 'dark'
                        ? 'bg-sidebar-primary-hover font-medium'
                        : 'bg-accent font-medium'
                      : '',
                  )}
                  asChild
                >
                  <Button
                    variant={theme === 'dark' ? 'botEnvironment' : 'ghost'}
                    size="sm"
                    className={cn(
                      'flex-1 rounded-2xl text-[15px]',
                      isActive && '[&_svg]:[stroke-width:1.75]',
                    )}
                  >
                    <span
                      className={cn(
                        theme === 'dark' ? 'text-white' : 'text-gray-500',
                      )}
                    >
                      {icon}
                    </span>
                  </Button>
                </TooltipTrigger>

                <TooltipContent side="right" className="text-sm">
                  {title}
                </TooltipContent>
              </Tooltip>
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="right" align="start" className="w-48">
            {subItems?.map((subItem) => (
              <SidebarSubMenuItem
                key={subItem.index}
                subItem={subItem}
                variant="dropdown"
                theme={theme}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </li>
    )
  },
)

SidebarDropdownItem.displayName = 'SidebarDropdownItem'
